import { IconButton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { ViewPage } from "../../../../../../assets/icons";
import { formatNumber } from "../../../../../../common/utils";
import {
	EntitiesListDTO,
	EntityItemDTO,
	MeasurableValueDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { headerCellMeasure } from "../../../../utils";

export const getRows = (data: EntitiesListDTO) => {
	if (!data) return [];

	return data.items.map((entity: Record<string, any>) => ({
		id: entity.id,
		description: entity.description,
		transportId: entity.transportId,
		transportDescription: entity.transportDescription,
		originId: entity.originId,
		originDescription: entity.originDescription,
		destinationId: entity.destinationId,
		destinationDescription: entity.destinationDescription,
		distance: entity.distance,
		weight: entity.weight,
		emissionWtw: entity.emissionWtw,
		emissionTtw: entity.emissionTtw,
		date: entity.date,
	}));
};

export const colsData = (): GridColDef[] => [
	{
		field: "description",
		headerName: "description",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ?? "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "originDescription",
		headerName: "departure",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ?? "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "destinationDescription",
		headerName: "arrival",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ?? "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "distance",
		headerName: "distance",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},

	{
		field: "weight",
		headerName: "weight",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},

	{
		field: "emissionWtw",
		headerName: "emissions",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},

	{
		field: "emissionTtw",
		headerName: "emissions",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center" alignItems="end">
				<Typography variant="body2">
					{params.value ? formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2) : "-"}
				</Typography>
			</Stack>
		),
	},

	{
		field: "transportDescription",
		headerName: "transport",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ?? "-"}</Typography>
			</Stack>
		),
	},

	{
		field: "date",
		headerName: "date",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Stack height="100%" justifyContent="center">
				<Typography variant="body2">{params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-"}</Typography>
			</Stack>
		),
	},
];

export const getColumns = (
	headerTextColor: string,
	t: TFunction,
	onNavigate: (row: EntityItemDTO) => void,
	dataSample?: Record<string, any>
): GridColDef[] => {
	const actionCol: GridColDef = {
		field: "actions",
		headerName: t("common:actions"),
		hideable: true,
		sortable: false,
		width: 100,
		align: "center",
		renderHeader: () => (
			<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="body2">
				{t("common:actions")}
			</Typography>
		),
		renderCell: (params) => (
			<IconButton
				onClick={(e) => {
					e.stopPropagation();
					onNavigate(params.row);
				}}
			>
				<ViewPage fontSize="small" />
			</IconButton>
		),
	};

	const baseCols = colsData().map((col) => {
		const label = t(`common:tables header/${col.headerName}`);

		return {
			...col,
			headerName: label,

			renderHeader: () => (
				<Stack direction="row" spacing={1} alignItems="baseline">
					<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="body2">
						{label}
					</Typography>

					<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="caption">
						{headerCellMeasure(col, dataSample || {})}
					</Typography>
				</Stack>
			),
		};
	});

	return [...baseCols, actionCol];
};
